import { datadogLogs } from '@datadog/browser-logs';
import { config } from "node-config-ts";

/**
 * Data Dog Logging Service.
 *
 * Wraps the logging functions of Data Dog in ES6 class to make
 * things a little smoother.
 * Follows the Data Dog browser API as closely as possible.
 */
export class LoggerService {
	environment;
	enableProductionLogging = process.env.NODE_ENV === 'production';
	enableInfoLogging = config.REACT_APP_ENABLE_INFO_LOG;
	enableWarnLogging = config.REACT_APP_ENABLE_WARN_LOG;

	constructor() {
		this.enableProductionLogging = process.env.NODE_ENV === 'production';
		this.environment = config.APPLICATION_ENVIRONMENT;
	}

	toggleLogging(isEnabled: boolean) {
		this.enableProductionLogging = isEnabled;
		this.enableInfoLogging = isEnabled;
		this.enableWarnLogging = isEnabled;
	}

	handleError(err: any) {
		if (process.env.NODE_ENV === 'production') {
			this.error(err.originalError || err);
		}
		throw err;
	}

	/**
	 * Logs a message to Data Dog at the default log level
	 *
	 *  {String} message The content of the message
	 *  {Error} [error] An exception to pass along with the message
	 *  {Object} [data] Custom data object to log with the message
	 *  {Function} [callback] If you aren't into promises, provide a callback.
	 *
	 *
	 *  LoggerService
	 */
	log(message: any, error?: any, data?: any): Promise<void> {
		if (this.enableProductionLogging) {
			return new Promise((resolve) => {
				datadogLogs.logger.log(message, {
					content: message,
					error,
					data
				});
				resolve();
			});
		}

		return Promise.resolve();
	}

	/**
	 * Logs an info level log message to Data Dog
	 *
	 *  {String} message The content of the message
	 *  {Error} [error] An exception to pass along with the message
	 *  {Object} [data] Custom data object to log with the message
	 *  {Function} [callback] If you aren't into promises, provide a callback.
	 *
	 *
	 *  LoggerService
	 */
	info(message: any, data?: any): Promise<void> {
		if (this.enableInfoLogging) {
			return new Promise((resolve) => {
				datadogLogs.logger.info(message, {
					content: message,
					data
				});
				resolve();
			});
		}

		return Promise.resolve();
	}

	/**
	 * Logs a WARN level log message to Data Dog
	 *
	 *  {String} message The content of the message
	 *  {Error} [error] An exception to pass along with the message
	 *  {Object} [data] Custom data object to log with the message
	 *  {Function} [callback] If you aren't into promises, provide a callback.
	 *
	 *
	 *  LoggerService
	 */
	warn(message: any, error?: any, data?: any): Promise<void> {
		if (this.enableWarnLogging) {
			return new Promise((resolve) => {
				datadogLogs.logger.warn(message, {
					content: message,
					error,
					data
				});
				resolve();
			});
		}

		return Promise.resolve();
	}

	/**
	 * Logs an ERROR level log message to Data Dog
	 *
	 *  {String} message The content of the message
	 *  {Error} [error] An exception to pass along with the message
	 *  {Object} [data] Custom data object to log with the message
	 *  {Function} [callback] If you aren't into promises, provide a callback.
	 *
	 *
	 *  LoggerService
	 */
	error(message: any, error?: any, data?: any): Promise<void> {
		if (this.enableProductionLogging) {
			return new Promise((resolve) => {
				datadogLogs.logger.error(message, {
					content: message,
					error,
					data
				});
				resolve();
			});
		}

		return Promise.resolve();
	}

	/**
	 * Logs a CRITICAL ERROR log message to Data Dog
	 *
	 *  {String} message The content of the message
	 *  {Error} [error] An exception to pass along with the message
	 *  {Object} [data] Custom data object to log with the message
	 *  {Function} [callback] If you aren't into promises, provide a callback.
	 *
	 *
	 *  LoggerService
	 */
	critical(message: any, error?: any, data?: any): Promise<void> {
		if (this.enableProductionLogging) {
			return new Promise((resolve) => {
				datadogLogs.logger.error(message, {
					content: message,
					error,
					data
				});
				resolve();
			});
		}

		return Promise.resolve();
	}
}

export const Logger = new LoggerService();
